import { Dashboard, DASHBOARD_COLUMNS } from 'types/dashboard';
import { DashboardWidget } from 'types/dashboard_widget';

import { downloadFile } from 'services/download_file';

/**
 * Calculates and returns a new layout position for a widget on a dashboard.
 *
 * The new position is determined based on the existing layout of provided widgets,
 * the dimensions of the new widget, and the overall dashboard constraints (e.g., column count).
 *
 * @param {DashboardWidget[]} widgets - An array of existing dashboard widgets.
 * @param {number} w - The width of the new widget.
 * @param {number} h - The height of the new widget.
 * @returns The calculated layout for the new widget.
 */
export function getNewWidgetLayout(
  widgets: DashboardWidget[],
  w: number,
  h: number
) {
  const layout = { x: 0, y: 0, w, h };

  // Find the maximum x and y values among the existing widgets
  const maxX = widgets.reduce(
    (max, widget) => Math.max(max, widget.layout.x + widget.layout.w),
    0
  );
  const maxY = widgets.reduce(
    (max, widget) => Math.max(max, widget.layout.y + widget.layout.h),
    0
  );

  // If maxX + widget width exceeds the column count, put the widget at the start of a new row
  if (maxX + w > DASHBOARD_COLUMNS) {
    layout.x = 0;
    layout.y = maxY;
  } else {
    layout.x = maxX;
    layout.y = Math.floor(maxY / layout.h) * layout.h;
  }

  return layout;
}

/**
 * Updates a dashboard by either inserting a new widget or replacing an existing one with the matching ID.
 *
 * @param {Dashboard} dashboard - The original dashboard to be updated.
 * @param {DashboardWidget} widget - The widget to be inserted or used for updating an existing one.
 * @returns A cloned and updated dashboard with the new configuration.
 */
export function upsertWidget(dashboard: Dashboard, widget: DashboardWidget) {
  const updatedDashboard = structuredClone(dashboard);

  const index = updatedDashboard.configuration.widgets.findIndex(
    ({ id }) => id === widget.id
  );

  if (index !== -1) {
    // Update existing widget
    updatedDashboard.configuration.widgets[index] = widget;
  } else {
    // Add new widget
    updatedDashboard.configuration.widgets.push(widget);
  }

  return updatedDashboard;
}

/**
 * Exports a dashboard's configuration as a JSON file and triggers its download.
 *
 * @param {Dashboard} dashboard - The dashboard to be exported
 */
export function exportDashboardAsJson(dashboard: Dashboard) {
  const jsonString = JSON.stringify(dashboard.configuration);
  const blob = new Blob([jsonString], { type: 'application/json' });

  downloadFile(dashboard.name, blob);
}
